import { jwtDecode } from 'jwt-decode';
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { clearAllCacheExcept } from '../../utils/functions';

export const useGovBrAuth = (
  setLoginStatus: (msg: string) => void,
  setToastType: (type: 'success' | 'error' | 'info') => void,
  setShowToast: (show: boolean) => void,
) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const readParamEverywhere = (key: string): string | null => {
    const urlParam = new URL(window.location.href).searchParams.get(key);
    if (urlParam) return urlParam;
    const hash = window.location.hash || '';
    const [, hashQuery = ''] = hash.split('?');
    if (hashQuery) {
      const hashParams = new URLSearchParams(hashQuery);
      const v = hashParams.get(key);
      if (v) return v;
    }
    return searchParams.get(key);
  };

  const removeParamEverywhere = (key: string) => {
    const url = new URL(window.location.href);
    url.searchParams.delete(key);
    const hash = window.location.hash || '';
    const [hashPathOnly, hashQuery = ''] = hash.split('?');
    if (hashQuery) {
      const params = new URLSearchParams(hashQuery);
      params.delete(key);
      window.history.replaceState(
        {},
        document.title,
        `${url.origin}${url.pathname}${url.search}${hashPathOnly}${params.toString() ? `?${params.toString()}` : ''}`,
      );
      return;
    }
    window.history.replaceState({}, document.title, url.toString());
  };

  const handleGovBrError = (errorCode: string) => {
    const mensagens: Record<string, string> = {
      user_inactive:
        'Usuário não habilitado no sistema. Entre em contato com o administrador.',
      access_denied:
        'Acesso negado pelo Gov.br. Verifique as permissões e tente novamente.',
      invalid_request:
        'Falha ao processar a solicitação no Gov.br. Tente novamente.',
      server_error: 'Erro interno no Gov.br. Tente novamente mais tarde.',
      temporarily_unavailable:
        'Serviço temporariamente indisponível. Tente novamente mais tarde.',
    };
    const mensagemErro =
      mensagens[errorCode] ||
      `Erro inesperado na autenticação Gov.br (${errorCode}).`;
    setLoginStatus(mensagemErro);
    setToastType('error');
    setShowToast(true);
    removeParamEverywhere('error');
  };

  const getNextRouteFromTokenPayload = (decoded: any): string => {
    const papelArray = Array.isArray(decoded?.papel) ? decoded.papel : [];
    const papelNorm = papelArray.map((p: any) =>
      typeof p === 'string' ? p.toUpperCase() : p,
    );
    const rels: number[] = Array.isArray(decoded?.relacoesInterface)
      ? decoded.relacoesInterface.map((v: any) => Number(v))
      : [];
    const isAdmin =
      decoded?.isAdmin === true ||
      papelNorm.includes('ADMIN') ||
      papelNorm.includes(1);
    const isSES =
      decoded?.isSES === true ||
      rels.includes(3) ||
      !!decoded?.coSes ||
      (Array.isArray(decoded?.ses) && decoded.ses.length > 0);
    const isFornecedor =
      decoded?.isFornecedor === true ||
      rels.includes(2) ||
      !!decoded?.coFornecedor ||
      (Array.isArray(decoded?.fornecedores) && decoded.fornecedores.length > 0);
    const statusInterface = String(
      decoded?.statusInterface ?? 'S',
    ).toUpperCase();

    if (isSES)
      return statusInterface === 'N'
        ? '/secsaude/meusdados/usuario'
        : '/secsaude/home/inicio';
    if (isFornecedor)
      return statusInterface === 'N'
        ? '/fornecedor/meusdados/usuario'
        : '/fornecedor/home/inicio';
    if (isAdmin) return '/daf/home/inicio';
    return '/';
  };

  useEffect(() => {
    const error = readParamEverywhere('error');
    if (error) {
      handleGovBrError(error);
      return;
    }

    const token =
      readParamEverywhere('token') || readParamEverywhere('access_token');
    const refresh = readParamEverywhere('refresh_token');

    if (token) {
      try {
        const decoded: any = jwtDecode(token);
        const nextRoute = getNextRouteFromTokenPayload(decoded);

        localStorage.setItem('token', token);
        if (refresh) localStorage.setItem('refresh_token', refresh);
        clearAllCacheExcept();

        // limpa params
        removeParamEverywhere('token');
        removeParamEverywhere('access_token');
        removeParamEverywhere('refresh_token');

        navigate(nextRoute, { replace: true });
      } catch (err) {
        setLoginStatus(
          'Não foi possível validar o acesso do Gov.br. Tente novamente.',
        );
        setToastType('error');
        setShowToast(true);
      }
    }
  }, [searchParams, navigate]);
};
